import type { ReactElement } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { PimLink } from '@/features/shared/pim-link';
import { SupplierHoverCardInfoRow } from '@/features/shared/supplier-hover-card-info-row';
import { useLocalizedCountryName } from '@/hooks/use-localized-country';
import { cn } from '@/lib/utils';
import type { Supplier } from '@/types/core/purchase-planning/supplier';

type SupplierCardProps = {
  supplier: Supplier;
  className?: string;
};

export function SupplierHoverCard({
  supplier,
  className,
}: SupplierCardProps): ReactElement {
  const { t } = useTranslation('common');
  const { getLocalizedCountry } = useLocalizedCountryName();
  const location = [supplier.city, supplier.postalCode]
    .filter(Boolean)
    .join(', ');
  const countryName = supplier.country?.isoCode
    ? getLocalizedCountry(supplier.country.isoCode)
    : null;

  return (
    <HoverCard>
      <div className='flex items-center gap-1'>
        <HoverCardTrigger asChild onClick={(e) => e.stopPropagation()}>
          <div className='flex items-center gap-1'>
            {supplier.country?.isoCode && (
              <ReactCountryFlag
                countryCode={supplier.country.isoCode}
                style={{ width: '1.5em', height: '1.5em' }}
                svg
              />
            )}
            <span
              className={cn(
                'block cursor-pointer text-xs hover:underline',
                className,
              )}
            >
              {supplier.name ?? supplier.id}
            </span>
          </div>
        </HoverCardTrigger>
      </div>
      <HoverCardContent align='start' className='w-80'>
        <div className='space-y-4'>
          <div className='flex items-center gap-2'>
            {supplier.country?.isoCode && (
              <ReactCountryFlag
                countryCode={supplier.country.isoCode}
                style={{ width: '1.5em', height: '1.5em' }}
                svg
              />
            )}
            <h4 className='font-semibold'>{supplier.name ?? t('unknown')}</h4>
          </div>
          <div className='space-y-1.5'>
            <SupplierHoverCardInfoRow label={t('id')} value={supplier.id} />
            {location && (
              <SupplierHoverCardInfoRow
                label={t('location')}
                value={location}
              />
            )}
            {countryName && (
              <SupplierHoverCardInfoRow
                label={t('country')}
                value={countryName}
              />
            )}
          </div>
          <div className='space-y-1.5 border-t pt-2'>
            <SupplierHoverCardInfoRow
              label={t('replenishment time')}
              value={
                supplier.replenishmentTimes === null
                  ? null
                  : +supplier.replenishmentTimes
                    ? `${supplier.replenishmentTimes} ${t('days')}`
                    : t('none')
              }
            />
            <SupplierHoverCardInfoRow
              label={t('lead time production')}
              value={
                supplier.leadTimeProductionWeeks === null
                  ? null
                  : `${supplier.leadTimeProductionWeeks} ${t('weeks')}`
              }
            />
            <SupplierHoverCardInfoRow
              label={t('lead time delivery')}
              value={
                supplier.leadTimeDeliveryWeeks === null
                  ? null
                  : `${supplier.leadTimeDeliveryWeeks} ${t('weeks')}`
              }
            />
            <SupplierHoverCardInfoRow
              label={t('order point')}
              value={
                supplier.orderPoint === null
                  ? null
                  : `${supplier.orderPoint} ${t('weeks')}`
              }
            />
          </div>
          <div className='flex justify-end'>
            <PimLink
              className='text-blue-500 text-sm italic hover:underline'
              href={`/suppliers/${supplier.id}`}
            >
              {t('link to pim')}
            </PimLink>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
